/**
 * Admin · Customers (006 admin-api / 010 stores). The SINGLE customer view —
 * it merges what used to be two tabs (tenant "Customers" + super-admin "Stores"),
 * since a customer account *is* a workspace/store. It adapts to role:
 *   - super_admin → the cross-tenant workspace grid (every store + KPIs),
 *     backed by `dashboardStores()`.
 *   - tenant_admin → the tenant-confined account list with create + block/unblock,
 *     backed by `listCustomers()`.
 * Both open the same `CustomerDetailPanel` as a FULL PAGE (license, devices, KPIs,
 * live processes, plan/limits/views and the template-options editors — every control).
 * The server enforces role/tenant scope on every call (006/010); the UI mirrors.
 */
import { useMemo, useState, type ReactNode } from "react";
import { ActionBar } from "../ui/ActionBar.js";
import { EmptyState } from "../ui/EmptyState.js";
import { useServices } from "../app/context.js";
import { InfoHint } from "../ui/Tooltip.js";
import { useSessionStore } from "../stores/session.js";
import type { CustomerRecord, Role } from "@genz/platform";
import { StatusBadge } from "./detail-shared.js";
import { useCachedResource } from "../stores/useCachedResource.js";
import { StaleNotice } from "./StaleNotice.js";
import { CustomerDetailPanel } from "./CustomerDetailPanel.js";
import { CopyButton } from "../ui/CopyButton.js";
import { enumLabel } from "../format/labels.js";
import { Icon } from "../ui/Icon.js";
import { RowAction } from "../ui/RowAction.js";

/** Known pricing tiers — mirrors cloud `entitlements.service.TIERS`
 *  (DEFAULT_ENTITLEMENTS keys); the PATCH endpoint rejects anything else 400. */
const TIERS = ["free", "pro", "enterprise"] as const;
/** Roles a tenant-admin may assign at create time (super_admin is granted, not created here). */
const ROLES: Role[] = ["customer", "tenant_admin"];

type SortDir = "asc" | "desc";

/** A clickable, accessible column header that toggles a shared sort key/direction. */
function SortHeader<K extends string>({
  label, sortKey, active, dir, onSort, numeric,
}: {
  label: string;
  sortKey: K;
  active: K;
  dir: SortDir;
  onSort: (k: K) => void;
  numeric?: boolean;
}) {
  const isActive = active === sortKey;
  return (
    <th className={numeric ? "num" : undefined} aria-sort={isActive ? (dir === "asc" ? "ascending" : "descending") : "none"}>
      <button
        type="button"
        className="link-btn"
        onClick={() => onSort(sortKey)}
        data-testid={`customer-sort-${sortKey}`}
        aria-label={`Sort by ${label}`}
      >
        {label}{isActive ? <> <Icon name={dir === "asc" ? "arrow-up" : "arrow-down"} size={16} /></> : ""}
      </button>
    </th>
  );
}

/** Generic comparator: case-insensitive for strings, numeric for numbers. */
function compareBy<T>(a: T, b: T): number {
  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a ?? "").toLowerCase().localeCompare(String(b ?? "").toLowerCase());
}

/** Two-letter initials from a name or email, for the avatar cell. */
function initialsOf(value: string): string {
  const base = (value.split("@")[0] ?? value).replace(/[._-]+/g, " ").trim();
  const parts = base.split(/\s+/).filter(Boolean);
  const letters = parts.length >= 2 ? parts[0][0] + parts[1][0] : base.slice(0, 2);
  return (letters || "?").toUpperCase();
}

/** Avatar-initials + primary/secondary label cell (mock customers table). */
function CustomerCell({ primary, secondary, onOpen, testid }: {
  primary: string;
  secondary?: string;
  onOpen: () => void;
  testid?: string;
}) {
  return (
    <div className="cust-cell">
      {/* Uppercased HERE, not by CSS: a `text-transform` on a monogram is indistinguishable
          from an eyebrow to the gate, and a gate with a carve-out is a gate that rots. */}
      <span className="cust-avatar" aria-hidden="true">{initialsOf(primary).toUpperCase()}</span>
      <span className="cust-cell__meta">
        <button type="button" className="link-btn" onClick={onOpen} data-testid={testid}>
          {primary}
        </button>
        {secondary && <span className="cust-cell__sub">{secondary}</span>}
      </span>
    </div>
  );
}

/** Full-page (not a drawer) customer detail with a back link — hosts every control. */
function CustomerDetailFullPage({ title, subtitle, badge, customerId, onBack, onChanged }: {
  title: string;
  subtitle: string;
  badge?: ReactNode;
  customerId: string;
  onBack: () => void;
  onChanged: () => void;
}) {
  return (
    <section className="admin-tab customer-detail-page" data-testid="customer-detail-page">
      <div className="panel-head panel-head--detail">
        <div className="row customers__head-row">
          <button type="button" className="btn btn--sm btn--ghost" onClick={onBack} data-testid="customer-detail-back">
            <Icon name="arrow-left" size={16} /> Back to customers
          </button>
          <div>
            <h2>{title}</h2>
            <p className="panel-sub">{subtitle}</p>
          </div>
          {badge}
        </div>
      </div>
      <CustomerDetailPanel customerId={customerId} onChanged={onChanged} />
    </section>
  );
}

/** Matches the cloud `dashboard.stores()` row shape (super-admin cross-tenant view). */
interface StoreRow {
  customer_id: string;
  email: string;
  tenant_id: string | null;
  tier: string;
  status: string;
  device_count: number;
  job_count: number;
  license: { id: string; tier: string; seats: number } | null;
}

export function CustomersTab() {
  const isSuper = useSessionStore((s) => s.isSuperAdmin());
  return isSuper ? <StoresView /> : <TenantCustomersView />;
}

/** Super-admin: every workspace across tenants, with KPIs. */
function StoresView() {
  const { client } = useServices();
  const [detailFor, setDetailFor] = useState<StoreRow | null>(null);
  // Cache-first: keep the stores list on screen across tab switches; revalidate 60s.
  const { data, error, stale, revalidate } = useCachedResource<StoreRow[]>(
    "admin.stores", async () => (await client.dashboardStores()) as StoreRow[],
  );
  const rows = data ?? [];
  const refresh = revalidate;

  // Client-side search + filter + sort over the already-fetched list.
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortKey, setSortKey] = useState<"email" | "tier" | "device_count" | "job_count" | "status">("email");
  const [sortDir, setSortDir] = useState<SortDir>("asc");
  function onSort(k: typeof sortKey) {
    if (k === sortKey) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(k); setSortDir("asc"); }
  }
  const statuses = useMemo(
    () => Array.from(new Set(rows.map((r) => r.status).filter(Boolean))).sort(),
    [rows],
  );
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = rows.filter(
      (r) =>
        (q === "" || r.email.toLowerCase().includes(q)) &&
        (statusFilter === "all" || r.status === statusFilter),
    );
    const sorted = [...filtered].sort((a, b) => compareBy(a[sortKey], b[sortKey]));
    if (sortDir === "desc") sorted.reverse();
    return sorted;
  }, [rows, query, statusFilter, sortKey, sortDir]);

  if (detailFor) {
    return (
      <CustomerDetailFullPage
        title={detailFor.email}
        subtitle={`${detailFor.tier ?? "Customer"} · workspace`}
        badge={<StatusBadge status={detailFor.status} />}
        customerId={detailFor.customer_id}
        onBack={() => setDetailFor(null)}
        onChanged={refresh}
      />
    );
  }

  return (
    <section className="admin-tab" data-testid="admin-customers">
      {error && <p role="alert">{error}</p>}
      <StaleNotice stale={stale} testid="stores-stale" />
      <div className="admin-panel admin-panel--flush" data-testid="customers-panel">
        <div className="admin-panel__head">
          <div>
            <strong className="admin-panel__title panel-title">Customers &amp; stores <InfoHint text="Every customer workspace across tenants — click a row to open its KPIs, license, devices and live processes." /></strong>
          </div>
          {rows.length > 0 && (
            <div className="row customers__toolbar" role="search" data-testid="customers-toolbar">
              <input
                id="stores-search"
                className="admin-panel__search"
                type="search"
                aria-label="Search customers"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search name or email…"
                data-testid="customer-search"
              />
              <select
                id="stores-status"
                aria-label="Filter by status"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                data-testid="customer-status-filter"
              >
                <option value="all">All statuses</option>
                {statuses.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          )}
        </div>
        {rows.length === 0 ? (
          <EmptyState
            icon="settings"
            title="No customers to show"
            hint="Connected workspaces will be listed here."
            testId="customers-empty"
          />
        ) : visible.length === 0 ? (
          <EmptyState
            icon="settings"
            title="No customers match"
            hint="Try a different search or status filter."
            testId="customers-no-match"
          />
        ) : (
          <div className="table-scroll">
          <table className="table" data-testid="customers-table">
            <thead>
              <tr>
                <SortHeader label="Customer" sortKey="email" active={sortKey} dir={sortDir} onSort={onSort} />
                <SortHeader label="Tier" sortKey="tier" active={sortKey} dir={sortDir} onSort={onSort} />
                <SortHeader label="Devices" sortKey="device_count" active={sortKey} dir={sortDir} onSort={onSort} numeric />
                <SortHeader label="Jobs" sortKey="job_count" active={sortKey} dir={sortDir} onSort={onSort} numeric />
                <SortHeader label="Status" sortKey="status" active={sortKey} dir={sortDir} onSort={onSort} />
                <th className="row-chevron" aria-hidden="true" />
              </tr>
            </thead>
            <tbody>
              {visible.map((r) => (
                <tr key={r.customer_id} className="is-clickable" data-testid="customer-row" onClick={() => setDetailFor(r)}>
                  <td>
                    <CustomerCell primary={r.email} onOpen={() => setDetailFor(r)} testid="customer-open" />
                  </td>
                  <td>{r.tier ? <span className="badge badge--neutral">{enumLabel(r.tier)}</span> : <span className="faint">—</span>}</td>
                  <td className="num">{r.device_count ?? 0}</td>
                  <td className="num">{r.job_count ?? 0}</td>
                  <td><StatusBadge status={r.status} /></td>
                  <td className="row-chevron" aria-hidden="true">›</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>
    </section>
  );
}

/** Tenant-admin: the tenant's own accounts, with create + block/unblock. */
function TenantCustomersView() {
  const { client } = useServices();
  const [busy, setBusy] = useState<string | null>(null);

  // Create form
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState<Role>("customer");
  const [tier, setTier] = useState<string>("free");
  const [creating, setCreating] = useState(false);

  // List controls — client-side over the already-fetched accounts.
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [tierFilter, setTierFilter] = useState("all");
  const [sortKey, setSortKey] = useState<"email" | "tier" | "status">("email");
  const [sortDir, setSortDir] = useState<SortDir>("asc");
  function onSort(k: typeof sortKey) {
    if (k === sortKey) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(k); setSortDir("asc"); }
  }

  // Detail drawer
  const [detailFor, setDetailFor] = useState<CustomerRecord | null>(null);

  // Cache-first: keep the accounts list across tab switches; revalidate every 60s.
  const { data, loading, error: loadError, stale, revalidate } = useCachedResource<CustomerRecord[]>(
    "admin.customers", () => client.listCustomers(),
  );
  const rows = data ?? [];
  const refresh = revalidate;
  // Separate error channel for mutations (create / block).
  const [actionError, setActionError] = useState<string | null>(null);
  const setError = setActionError;
  const error = actionError ?? loadError;

  const tierOptions = useMemo(
    () => Array.from(new Set([...TIERS, ...rows.map((c) => c.tier).filter(Boolean)])),
    [rows],
  );
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = rows.filter(
      (c) =>
        (q === "" || c.email.toLowerCase().includes(q)) &&
        (statusFilter === "all" || c.status === statusFilter) &&
        (tierFilter === "all" || c.tier === tierFilter),
    );
    const sorted = [...filtered].sort((a, b) => compareBy(a[sortKey], b[sortKey]));
    if (sortDir === "desc") sorted.reverse();
    return sorted;
  }, [rows, query, statusFilter, tierFilter, sortKey, sortDir]);

  if (detailFor) {
    return (
      <CustomerDetailFullPage
        title={detailFor.email}
        subtitle="Customer"
        badge={<StatusBadge status={detailFor.status} />}
        customerId={detailFor.id}
        onBack={() => setDetailFor(null)}
        onChanged={refresh}
      />
    );
  }

  async function onCreate(e: React.FormEvent) {
    e.preventDefault();
    if (!email || !password) return;
    setCreating(true);
    setError(null);
    try {
      await client.createCustomer({ email, password, role, tier });
      setEmail("");
      setPassword("");
      setRole("customer");
      setTier("free");
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setCreating(false);
    }
  }

  async function toggleBlock(c: CustomerRecord) {
    setBusy(c.id);
    setError(null);
    try {
      if (c.status === "blocked") await client.unblockCustomer(c.id);
      else await client.blockCustomer(c.id);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  }

  return (
    <section className="admin-tab" data-testid="admin-customers">
      <form className="admin-tab__form" onSubmit={onCreate} data-testid="customer-create-form">
        <div className="field">
          <label htmlFor="cust-email">Email</label>
          <input
            id="cust-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="new@example.com"
            data-testid="customer-email"
            required
          />
        </div>
        <div className="field">
          <label htmlFor="cust-pass">Temporary password</label>
          <input
            id="cust-pass"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            data-testid="customer-password"
            required
          />
        </div>
        <div className="field">
          <label htmlFor="cust-role">Role</label>
          <select
            id="cust-role"
            value={role}
            onChange={(e) => setRole(e.target.value as Role)}
            data-testid="customer-role"
          >
            {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        <div className="field">
          <label htmlFor="cust-tier">Tier</label>
          <select
            id="cust-tier"
            value={tier}
            onChange={(e) => setTier(e.target.value)}
            data-testid="customer-tier"
          >
            {TIERS.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        {/* Rule 7 — the create form's commit, in the bar, and the tenant view's one
            primary. The table below it is a list of row actions, not a second invitation. */}
        <ActionBar
          label="Customer actions"
          primary={
            <button
              type="submit"
              className="btn btn--primary"
              disabled={creating || !email || !password}
              data-testid="customer-create"
            >
              {creating ? "Creating…" : "Create customer"}
            </button>
          }
        />
      </form>

      {error && <p role="alert">{error}</p>}
      <StaleNotice stale={stale} testid="customers-stale" />

      <div className="admin-panel admin-panel--flush" data-testid="customers-panel">
        <div className="admin-panel__head">
          <div>
            <strong className="admin-panel__title">Customers &amp; stores</strong>
            <p className="admin-panel__sub">Click a row to open the full detail page — license, devices, activity and live processes.</p>
          </div>
          {rows.length > 0 && (
            <div className="row customers__toolbar" role="search" data-testid="customers-toolbar">
              <input
                id="cust-search"
                className="admin-panel__search"
                type="search"
                aria-label="Search customers"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search name or email…"
                data-testid="customer-search"
              />
              <select
                id="cust-status-filter"
                aria-label="Filter by status"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                data-testid="customer-status-filter"
              >
                <option value="all">All statuses</option>
                <option value="active">active</option>
                <option value="blocked">blocked</option>
              </select>
              <select
                id="cust-tier-filter"
                aria-label="Filter by tier"
                value={tierFilter}
                onChange={(e) => setTierFilter(e.target.value)}
                data-testid="customer-tier-filter"
              >
                <option value="all">All tiers</option>
                {tierOptions.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          )}
        </div>

        {loading ? (
          <p className="faint" data-testid="customers-loading">Loading customers…</p>
        ) : rows.length === 0 ? (
          <EmptyState
            icon="settings"
            title="No customers yet"
            hint="Create your first customer account above."
            testId="customers-empty"
          />
        ) : visible.length === 0 ? (
          <EmptyState
            icon="settings"
            title="No customers match"
            hint="Try a different search or filter."
            testId="customers-no-match"
          />
        ) : (
          <div className="table-scroll">
          <table className="table" data-testid="customers-table">
            <thead>
              <tr>
                <SortHeader label="Email" sortKey="email" active={sortKey} dir={sortDir} onSort={onSort} />
                <th>Tenant</th>
                <SortHeader label="Tier" sortKey="tier" active={sortKey} dir={sortDir} onSort={onSort} />
                <SortHeader label="Status" sortKey="status" active={sortKey} dir={sortDir} onSort={onSort} />
                <th />
              </tr>
            </thead>
            <tbody>
              {visible.map((c) => (
                <tr key={c.id} data-testid="customer-row">
                  <td>
                    <CustomerCell primary={c.email} onOpen={() => setDetailFor(c)} testid="customer-open" />
                  </td>
                  <td>
                    {c.tenant_id ? (
                      <span className="id-cell">
                        <code>{c.tenant_id}</code>
                        <CopyButton value={c.tenant_id} label="Copy tenant id" />
                      </span>
                    ) : (
                      <span className="faint">—</span>
                    )}
                  </td>
                  <td><span className="badge badge--neutral">{enumLabel(c.tier)}</span></td>
                  <td><StatusBadge status={c.status} /></td>
                  <td>
                    <div className="row">
                      <RowAction
                        label="Manage"
                        hint="Manage this customer"
                        icon="arrow-right"
                        tone="quiet"
                        onClick={() => setDetailFor(c)}
                        data-testid="customer-manage"
                      />
                      {/* WS-Z9: Block and Unblock are two states of one control, so they take
                          two icons — a closed padlock and an open one. As words the tier did
                          the telling (danger vs plain); as icons the GLYPH does, and the tier
                          only follows it. */}
                      <RowAction
                        label={c.status === "blocked" ? "Unblock" : "Block"}
                        icon={c.status === "blocked" ? "unlock" : "lock"}
                        tone={c.status === "blocked" ? "default" : "danger"}
                        onClick={() => toggleBlock(c)}
                        disabled={busy === c.id}
                        data-testid="customer-block"
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>
    </section>
  );
}
